import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ControlCenter } from "@/features/admin";
import { Link } from "react-router-dom";
import {
  compact,
  countryName,
  flag,
  languageName,
  percent,
  SOURCE_LABEL,
} from "./format";

/** Vertical bars drawn in CSS: seven buckets do not need a chart library. */
export function Histogram({
  title,
  description,
  buckets,
  color,
  unit,
}: {
  title: string;
  description: string;
  buckets: { bucket: string; count: number }[];
  color: string;
  unit: string;
}) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const total = buckets.reduce((s, b) => s + b.count, 0);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        {total ? (
          <div className="flex h-[150px] items-end gap-2">
            {buckets.map((b) => (
              <div
                key={b.bucket}
                className="group flex h-full flex-1 flex-col items-center justify-end gap-1"
                title={`${b.count} · ${percent(b.count, total)}`}
              >
                <span className="text-[10px] tabular-nums text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
                  {percent(b.count, total)}
                </span>
                <div
                  className="w-full rounded-t-md transition-all duration-700"
                  style={{
                    height: `${Math.max(2, (b.count / max) * 100)}%`,
                    background: `linear-gradient(to top, color-mix(in srgb, ${color} 55%, transparent), ${color})`,
                    opacity: b.count ? 1 : 0.25,
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-[150px] items-center justify-center text-sm text-muted-foreground">
            Pas de données.
          </div>
        )}
        <div className="mt-1.5 flex gap-2">
          {buckets.map((b) => (
            <div
              key={b.bucket}
              className="flex-1 text-center text-[10px] text-muted-foreground"
            >
              {b.bucket}
            </div>
          ))}
        </div>
        <div className="mt-1 text-right text-[10px] text-muted-foreground">
          {unit}
        </div>
      </CardContent>
    </Card>
  );
}

function RankList({
  rows,
}: {
  rows: { key: string; label: React.ReactNode; value: number; hint?: string }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length)
    return <div className="py-2 text-xs text-muted-foreground">—</div>;
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.key} className="text-xs">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="min-w-0 truncate">{r.label}</span>
            <span className="ml-auto tabular-nums font-medium">
              {compact(r.value)}
            </span>
            {r.hint ? (
              <span className="w-9 shrink-0 text-right tabular-nums text-muted-foreground">
                {r.hint}
              </span>
            ) : null}
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-gradient-to-r from-chart-1 to-chart-2 transition-all duration-700"
              style={{ width: `${(r.value / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function RankSection({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

/**
 * Where the conversations come from, in a third of the row it shares with the feed and the
 * live list: countries and languages side by side (short labels), channels across the
 * whole width (long ones), five rows each — the same height as its two neighbours.
 */
export function Origins({ data }: { data: ControlCenter }) {
  const total = data.totals.sessions;
  return (
    <Card className="h-full">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Pays, canaux et langues</CardTitle>
        <span className="text-xs text-muted-foreground tabular-nums">
          {compact(total)} conversation(s)
        </span>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[360px] px-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
          <div className="grid grid-cols-2 gap-x-4 gap-y-4 pb-4">
            <RankSection title="Pays">
              <RankList
                rows={data.countries.slice(0, 5).map((c) => ({
                  key: c.country_code,
                  label: (
                    <>
                      <span className="mr-1">{flag(c.country_code)}</span>
                      {countryName(c.country_code)}
                    </>
                  ),
                  value: c.calls + c.chats,
                  hint: percent(c.calls + c.chats, total),
                }))}
              />
            </RankSection>
            <RankSection title="Langues">
              <RankList
                rows={data.languages.slice(0, 5).map((l) => ({
                  key: l.language,
                  label: languageName(l.language),
                  value: l.count,
                  hint: percent(l.count, total),
                }))}
              />
            </RankSection>
            <RankSection title="Canaux" className="col-span-2">
              <RankList
                rows={data.sources.map((s) => ({
                  key: s.key,
                  label: SOURCE_LABEL[s.key] ?? s.key,
                  value: s.count,
                  hint: percent(s.count, total),
                }))}
              />
            </RankSection>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

export function TopTenants({ data }: { data: ControlCenter }) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Tenants les plus actifs</CardTitle>
      </CardHeader>
      <CardContent>
        {data.top_tenants.length ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="pb-2 text-left font-medium">Client</th>
                <th className="pb-2 text-right font-medium">Conv.</th>
                <th className="pb-2 text-right font-medium">Appels</th>
                <th className="pb-2 text-right font-medium">RDV pris</th>
                <th className="pb-2 text-right font-medium">Transferts</th>
                <th className="pb-2 text-right font-medium">Réussite</th>
                <th className="pb-2 text-right font-medium">Live</th>
              </tr>
            </thead>
            <tbody>
              {data.top_tenants.map((t) => {
                const rate = t.closed ? t.success / t.closed : null;
                return (
                  <tr key={t.client_id} className="border-t">
                    <td className="max-w-[160px] truncate py-1.5">
                      <Link
                        to={`/admin/clients/${t.client_id}`}
                        className="hover:underline"
                      >
                        {t.client_name || t.client_id}
                      </Link>
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {compact(t.sessions)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {compact(t.calls)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {compact(t.bookings)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {compact(t.transfers)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {rate === null ? (
                        "—"
                      ) : (
                        <span
                          className={
                            rate >= 0.7
                              ? "text-success"
                              : rate >= 0.5
                                ? "text-warning"
                                : "text-danger"
                          }
                        >
                          {Math.round(rate * 100)} %
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {t.live ? (
                        <span className="inline-flex items-center gap-1 text-success">
                          <span className="size-1.5 rounded-full bg-success animate-pulse" />
                          {t.live}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Aucune activité.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
